import Swiper from 'swiper';
import { CRAWL_PER_SEC, MAX_DELTA, foldCrawl } from './utils-crawl';

const DESIGN_WIDTH = 1728;
const SLIDE_GAP = 48;
const SLIDE_OFFSET = 30;

/** 矢印で1枚送るときの尺(ms) */
const NAV_SPEED = 800;

/** 矢印を押したあと、また流れ始めるまでの待ち(ms) */
const RESUME_DELAY = 3000;

/**
 * デザイン基準幅のpx値を、現在のビューポート幅に応じたpx値へ変換する
 *
 * @param {number} value デザイン上のpx値
 * @returns {number} 変換後のpx値
 */
const toVwPx = (value: number): number => (window.innerWidth * value) / DESIGN_WIDTH;

/** モーションを抑える設定か */
const prefersReduced = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// 生きているSwiperと後片付け。非同期遷移で古いDOMを捨てるとき、
// rAFループやリスナーが外れた要素を触り続けないように持っておく
let instance: Swiper | null = null;
let teardown: (() => void) | null = null;

/**
 * スライダーを破棄する
 *
 * @returns {void}
 */
const destroyInstaSlider = (): void => {
    teardown?.();
    teardown = null;

    try {
        instance?.destroy(true, true);
    } catch {
        // 遷移で要素ごと外れている場合の後片付けは失敗しても構わない
    }

    instance = null;
};

/**
 * Instagram投稿の横スライダーを初期化する
 *
 * @description 止まらずにゆっくり横へ流し続ける。カーソルが乗っているあいだは止め、
 *              矢印で送ったあとは少し置いてから流れに戻す。ページを動かしたときは
 *              待ちを明けてすぐ戻す。
 * @returns {void}
 */
const initInstaSlider = (): void => {
    const el = document.querySelector<HTMLElement>('.Instaslider');

    if (!el) return;

    destroyInstaSlider();

    const total = el.querySelectorAll('.swiper-slide').length;

    const swiper = new Swiper(el, {
        slidesPerView: 'auto',
        loop: true,
        // slidesPerView:'auto' のループは複製数が足りないと隙間ができるので全スライドを複製させる
        loopedSlides: total,
        speed: NAV_SPEED,
        spaceBetween: toVwPx(SLIDE_GAP),
        slidesOffsetBefore: toVwPx(SLIDE_OFFSET),
        slidesOffsetAfter: toVwPx(SLIDE_OFFSET),
    });

    // loop と slidesOffsetBefore を併用すると初期位置が1枚ぶんズレる。
    // 複製ではなく実体の1枚目（前に複製が total 枚あるのでその次）へ明示的に置く
    swiper.slideTo(total, 0, false);

    /** 複製1組ぶんの幅。ここまで流したら同じ絵柄へ戻せるので、継ぎ目なくやり直せる */
    const setWidth = (): number => swiper.slidesGrid[total] - swiper.slidesGrid[0];

    /**
     * 流し続ける居場所の右端。複製ではなく実体の1組の先頭に置く
     *
     * @description 前後の複製の上に居ると、矢印を押した瞬間にSwiperが実体側へ引き戻す（loopFix）ので
     *              1枚ぶんのはずが1組ぶん飛ぶ。常に実体の組の中に畳んでおけばそれが起きない。
     * @returns {number} 実体1枚目のtranslate
     */
    const anchor = (): number => -swiper.slidesGrid[total];

    let frame = 0;
    let prev = 0;
    let hovering = false;
    let holdUntil = 0;
    let paused = false;

    /**
     * いま流してよいか
     *
     * @returns {boolean} 止めておらず、カーソルも乗っておらず、矢印の余韻も明けていれば true
     */
    const canCrawl = (): boolean => !paused && !hovering && performance.now() >= holdUntil;

    /**
     * 1フレームぶん左へ流す
     *
     * @param {number} now rAFのタイムスタンプ
     * @returns {void}
     */
    const tick = (now: number): void => {
        const delta = Math.min((now - prev) / 1000, MAX_DELTA);
        prev = now;

        // 送っている最中に割り込むと動きが途切れるので、その間は触らない
        if (!swiper.animating) {
            const from = swiper.getTranslate();
            const width = setWidth();
            const right = anchor();
            // 常に実体の1組の中へ畳む
            const next = foldCrawl(canCrawl() ? from - toVwPx(CRAWL_PER_SEC) * delta : from, right, width);

            if (next !== from) {
                swiper.setTransition(0);
                swiper.setTranslate(next);
                swiper.updateActiveIndex();
                swiper.updateSlidesClasses();
            }
        }

        frame = requestAnimationFrame(tick);
    };

    /**
     * 矢印で1枚送り、しばらく流れを止める
     *
     * @param {number} dir 1で次へ、-1で前へ
     * @returns {void}
     */
    const nav = (dir: number): void => {
        holdUntil = performance.now() + RESUME_DELAY;

        if (dir > 0) {
            swiper.slideNext(NAV_SPEED);
        } else {
            swiper.slidePrev(NAV_SPEED);
        }
    };

    /**
     * rAFループを回し始める
     *
     * @returns {void}
     */
    const startCrawl = (): void => {
        if (frame !== 0) return;

        prev = performance.now();
        frame = requestAnimationFrame(tick);
    };

    const pauseBtn = document.querySelector<HTMLElement>('.insta__bodyNavBtn.attr-pause');

    /**
     * 流れを止める／戻す
     *
     * @description ホバーの一時停止と違って、もう一度押すまで解けない
     * @param {boolean} next 止めるなら true
     * @returns {void}
     */
    const setPaused = (next: boolean): void => {
        paused = next;

        pauseBtn?.classList.toggle('is-paused', paused);
        pauseBtn?.setAttribute('aria-pressed', String(paused));
        pauseBtn?.setAttribute('aria-label', paused ? '自動再生を始める' : '自動再生を止める');

        // モーションを抑える設定だとループを回していないので、戻すときに始める
        if (!paused) startCrawl();
    };

    const onPrev = (): void => nav(-1);
    const onNext = (): void => nav(1);
    const onPause = (): void => setPaused(!paused);
    const onEnter = (): void => { hovering = true; };
    const onLeave = (): void => { hovering = false; };

    // ページを動かしたということは、もうこの列を見ていない。待ちを明けて流れに戻す
    const onPageScroll = (): void => { holdUntil = 0; };

    const prevBtn = document.querySelector<HTMLElement>('.insta__bodyNavBtn.attr-prev');
    const nextBtn = document.querySelector<HTMLElement>('.insta__bodyNavBtn.attr-next');

    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);
    pauseBtn?.addEventListener('click', onPause);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    window.addEventListener('scroll', onPageScroll, { passive: true });

    // 指やトラックパッドで掴んでいるあいだも流さない。離してからは矢印と同じ余韻を置く
    swiper.on('touchStart', onEnter);
    swiper.on('touchEnd', () => {
        hovering = false;
        holdUntil = performance.now() + RESUME_DELAY;
    });

    const onResize = (): void => {
        swiper.params.spaceBetween = toVwPx(SLIDE_GAP);
        swiper.params.slidesOffsetBefore = toVwPx(SLIDE_OFFSET);
        swiper.params.slidesOffsetAfter = toVwPx(SLIDE_OFFSET);
        swiper.update();
    };

    window.addEventListener('resize', onResize);

    // モーションを抑える設定なら、止めた状態から始める
    setPaused(prefersReduced());

    instance = swiper;
    teardown = () => {
        if (frame !== 0) cancelAnimationFrame(frame);
        frame = 0;
        window.removeEventListener('resize', onResize);
        prevBtn?.removeEventListener('click', onPrev);
        nextBtn?.removeEventListener('click', onNext);
        pauseBtn?.removeEventListener('click', onPause);
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
        window.removeEventListener('scroll', onPageScroll);
    };
};

export { initInstaSlider, destroyInstaSlider };

document.addEventListener('DOMContentLoaded', initInstaSlider);
